"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, Pause, Play, Volume2, VolumeX } from "lucide-react";
import {
  buildUtmUrl,
  detectHostDomainFromReferrer,
} from "@/utils/utm";

interface EmbedPlayerProps {
  audioUrl: string;
  title: string;
  /** Public Podcastify URL of this episode (deep link from the title row). */
  podcastUrl: string;
  /** Brand link target for the bottom attribution — defaults to the home. */
  brandUrl?: string;
  /** "light" | "dark" — picked from ?theme= query param. Defaults to light. */
  theme?: "light" | "dark";
}

const NUM_BARS = 48;

// Public canonical origin — same convention as the rest of the app. Inlined
// at build time since this is a client component.
const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://podcastify.io";

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Deterministic pseudo-random heights for the static "waveform" decoration.
 * Same seed for every podcast so the visual is consistent across renders.
 */
function buildWaveformHeights(): number[] {
  const heights: number[] = [];
  let seed = 8675309;
  for (let i = 0; i < NUM_BARS; i++) {
    seed = (seed * 9301 + 49297) % 233280;
    const rnd = seed / 233280;
    // Bias towards the middle of the bar range so the waveform looks "real".
    const wave = Math.sin((i / NUM_BARS) * Math.PI) * 0.4 + 0.6;
    heights.push(0.25 + rnd * 0.55 * wave);
  }
  return heights;
}

export function EmbedPlayer({
  audioUrl,
  title,
  podcastUrl,
  brandUrl = `${SITE_URL}/`,
  theme = "light",
}: EmbedPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [bars] = useState<number[]>(() => buildWaveformHeights());

  // Unified UTM scheme for every escape link out of the iframe:
  //   utm_source   = embed               (rolls up the embed iframe surface)
  //   utm_medium   = iframe              (distinguishes from explore/landing)
  //   utm_campaign = podcast_share       (the high-level growth campaign)
  //   utm_content  = title | brand_footer (per-element attribution)
  //   utm_term     = <host domain>       (which site embedded us; from referrer)
  // The host domain is read from document.referrer — inside the iframe that's
  // the page containing the <iframe>, which is the attribution signal we
  // actually want. It's read in an effect (not the useState initializer) so
  // the server-rendered HTML and the first client render match exactly,
  // avoiding a hydration mismatch when the referrer is present.
  const [hostDomain, setHostDomain] = useState<string | null>(null);
  useEffect(() => {
    setHostDomain(detectHostDomainFromReferrer());
  }, []);

  const utms = useMemo(
    () => ({
      utm_source: "embed",
      utm_medium: "iframe",
      utm_campaign: "podcast_share",
      ...(hostDomain ? { utm_term: hostDomain } : {}),
    }),
    [hostDomain],
  );
  const trackedPodcastUrl = useMemo(
    () => buildUtmUrl(podcastUrl, { ...utms, utm_content: "title" }),
    [podcastUrl, utms],
  );
  const trackedBrandUrl = useMemo(
    () => buildUtmUrl(brandUrl, { ...utms, utm_content: "brand_footer" }),
    [brandUrl, utms],
  );

  /* ---- audio event wiring ---- */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => {
      setDuration(audio.duration || 0);
      setIsLoading(false);
    };
    const onEnd = () => setIsPlaying(false);
    const onCanPlay = () => setIsLoading(false);
    // If the audio errors out we still need to release the disabled state on
    // the play button — otherwise a broken URL strands the user with a
    // wait-cursor button forever.
    const onError = () => setIsLoading(false);

    // The <audio preload="metadata"> element starts loading the moment React
    // renders it, which on same-origin static files (e.g. the /public demo
    // MP3) means `loadedmetadata` and `canplay` can fire BEFORE this effect
    // runs and attaches the listeners. In that case the listeners never see
    // those events, `isLoading` stays true, and the play button is stuck
    // disabled. Sync the state from `readyState` defensively before wiring up.
    if (audio.readyState >= 1 /* HAVE_METADATA */) {
      setDuration(audio.duration || 0);
      setIsLoading(false);
    }

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("error", onError);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play().catch(() => {
        setIsPlaying(false);
        setIsLoading(false);
      });
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  }, []);

  const seekFromBar = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.min(
        1,
        Math.max(0, (e.clientX - rect.left) / rect.width),
      );
      audio.currentTime = ratio * duration;
      setCurrentTime(audio.currentTime);
    },
    [duration],
  );

  const progress = duration > 0 ? currentTime / duration : 0;

  const isDark = theme === "dark";

  /* All styling lives inline so the embed remains self-contained — no global
     stylesheet leaks into the host page, no Tailwind reset, no FOUC. */
  const palette = isDark
    ? {
        bg: "#0b0b0f",
        surface: "rgba(255,255,255,0.04)",
        border: "rgba(255,255,255,0.08)",
        text: "#f5f5f7",
        muted: "rgba(245,245,247,0.6)",
        accent: "#fde047",
        barIdle: "rgba(255,255,255,0.18)",
        ring: "rgba(253,224,71,0.25)",
      }
    : {
        bg: "#ffffff",
        surface: "#fafaf9",
        border: "rgba(15,23,42,0.08)",
        text: "#0f172a",
        muted: "rgba(15,23,42,0.6)",
        accent: "#eab308",
        barIdle: "rgba(15,23,42,0.12)",
        ring: "rgba(234,179,8,0.25)",
      };

  return (
    <div
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, Roboto, sans-serif",
        background: palette.bg,
        color: palette.text,
        borderRadius: 16,
        border: `1px solid ${palette.border}`,
        padding: 16,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.4)"
          : "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -16px rgba(15,23,42,0.12)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Title row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 0,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: palette.accent,
            color: isDark ? "#0b0b0f" : "#0f172a",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {/* Mic glyph mirrors the landing-page header logo so the brand
              identity stays consistent across the marketing site and the
              embeddable player. */}
          <Mic size={18} strokeWidth={2.25} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <a
            href={trackedPodcastUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
            style={{
              display: "block",
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.25,
              color: palette.text,
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </a>
          <div
            style={{
              fontSize: 11,
              color: palette.muted,
              marginTop: 2,
            }}
          >
            AI podcast · powered by Podcastify
          </div>
        </div>
      </div>

      {/* Player row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          type="button"
          aria-label={isPlaying ? "Pause" : "Play"}
          onClick={togglePlay}
          disabled={isLoading}
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: "none",
            background: palette.accent,
            color: isDark ? "#0b0b0f" : "#0f172a",
            cursor: isLoading ? "wait" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 0 0 4px ${palette.ring}`,
            transition: "transform 120ms ease",
          }}
          onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.94)")}
          onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          {isPlaying ? (
            <Pause size={18} strokeWidth={2.5} fill="currentColor" />
          ) : (
            <Play
              size={18}
              strokeWidth={2.5}
              fill="currentColor"
              style={{ marginLeft: 2 }}
            />
          )}
        </button>

        {/* Waveform / progress bar */}
        <div
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
          tabIndex={0}
          onClick={seekFromBar}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            height: 36,
            cursor: duration ? "pointer" : "default",
            position: "relative",
          }}
        >
          {bars.map((h, i) => {
            const filled = i / NUM_BARS <= progress;
            return (
              <span
                key={i}
                style={{
                  display: "block",
                  width: `calc((100% - ${(NUM_BARS - 1) * 2}px) / ${NUM_BARS})`,
                  height: `${h * 100}%`,
                  borderRadius: 2,
                  background: filled ? palette.accent : palette.barIdle,
                  transition: "background 120ms ease",
                }}
              />
            );
          })}
        </div>

        <div
          style={{
            fontSize: 11,
            fontVariantNumeric: "tabular-nums",
            color: palette.muted,
            minWidth: 78,
            textAlign: "right",
          }}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        <button
          type="button"
          aria-label={isMuted ? "Unmute" : "Mute"}
          onClick={toggleMute}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `1px solid ${palette.border}`,
            background: palette.surface,
            color: palette.text,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
      </div>

      {/* Footer attribution — escape link out of the iframe back to the
          home page. We open in a new tab (target=_blank) so the host's
          page state isn't lost when a visitor clicks through. */}
      <a
        href={trackedBrandUrl}
        target="_blank"
        rel="noopener noreferrer"
        // Inline opacity transition without JS — :hover via onMouseEnter/Leave.
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.75")}
        style={{
          marginTop: 2,
          paddingTop: 8,
          borderTop: `1px solid ${palette.border}`,
          fontSize: 10,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: palette.muted,
          textDecoration: "none",
          alignSelf: "stretch",
          textAlign: "center",
          fontWeight: 600,
          opacity: 0.75,
          transition: "opacity 120ms ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
        }}
      >
        Generated with{" "}
        <span style={{ color: palette.accent, fontWeight: 800 }}>
          Podcastify
        </span>
        {/* tiny external-link glyph — pure SVG, no icon import bloat */}
        <svg
          aria-hidden
          width="9"
          height="9"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginLeft: 1, opacity: 0.6 }}
        >
          <path d="M4.5 2.5h-2v7h7v-2" />
          <path d="M7 2.5h2.5V5" />
          <path d="M9.5 2.5L5.5 6.5" />
        </svg>
      </a>
    </div>
  );
}
